'use client';

import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider, focusManager, onlineManager } from '@tanstack/react-query';

export function QueryProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Custom focus manager listener for iOS Safari and Home Screen Standalone PWA
    focusManager.setEventListener((handleFocus) => {
      if (typeof window === 'undefined' || !window.addEventListener) return () => {};

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          handleFocus(true);
        }
      };

      const onPageShow = () => {
        handleFocus(true);
      };

      const onFocus = () => {
        handleFocus(true);
      };

      window.addEventListener('visibilitychange', onVisibilityChange, false);
      window.addEventListener('pageshow', onPageShow, false);
      window.addEventListener('focus', onFocus, false);

      return () => {
        window.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pageshow', onPageShow);
        window.removeEventListener('focus', onFocus);
      };
    });

    // Custom online manager listener for reconnection
    onlineManager.setEventListener((setOnline) => {
      if (typeof window === 'undefined' || !window.addEventListener) return () => {};

      const onOnline = () => setOnline(true);
      const onOffline = () => setOnline(false);

      window.addEventListener('online', onOnline, false);
      window.addEventListener('offline', onOffline, false);

      return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      };
    });
  }, []);

  // Ensure we only create a new query client once per session on the client
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 1000, // 5 seconds freshness window
        refetchOnWindowFocus: true, // Refetch when app foregrounds / window focuses
        refetchOnReconnect: true,  // Refetch when network connection restores
        retry: 1,
      },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
